import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import formatDatasourceExecutedAt from '../utils/formatDatasourceExecutedAt';
import AiChatDatasourceEmpty from './AiChatDatasourceEmpty';
import AiChatDatasourceGrid from './AiChatDatasourceGrid';

import type { ReactElement, ReactNode } from 'react';
import type IAiChatChartFrame from '../interfaces/IAiChatChartFrame';

import styles from '../sass/AiChat.module.scss';

/**
 * What every chart of a stored result sits in: the title and the row count, a note for what the
 * chart left out and when the rows are from, and a switch to the rows themselves. The table is
 * always one click away, so a value the chart only hints at can be read exactly, and a reader who
 * cannot tell two colours apart still has the numbers. Rows the route cannot make again leave the
 * frame in place, with the route's reason where the chart would be.
 */
function AiChatChartFrame({
  datasourceId, title, columns, rowCount, truncated, isLoading, isError, executedAt, droppedReason, note, children,
}: IAiChatChartFrame): ReactElement {
  const { t } = useTranslation();
  const [isTable, setIsTable] = useState<boolean>(false);

  const handleToggle = () => setIsTable((current) => !current);

  const renderBody = (): ReactNode => {
    if (droppedReason !== null) {
      return <AiChatDatasourceEmpty reason={droppedReason} />;
    }

    if (isTable) {
      return <AiChatDatasourceGrid datasourceId={datasourceId} columns={columns} rowCount={rowCount} />;
    }

    if (isLoading) {
      return <p className={styles.note}>{t('aiChat.tools.datasourceLoading')}</p>;
    }

    if (isError) {
      return <p className={styles.error}>{t('aiChat.tools.datasourceLoadFailed')}</p>;
    }

    return children;
  };

  return (
    <div className={styles.datasource}>
      <div className={styles.toolHeader}>
        <p className={styles.datasourceTitle}>{title}</p>
        <p className={styles.toolStatus}>{t('aiChat.tools.datasourceRows', { count: rowCount })}</p>
        {droppedReason === null ? (
          <button type="button" className={styles.chartToggle} onClick={handleToggle}>
            {t(isTable ? 'aiChat.tools.chartShowChart' : 'aiChat.tools.chartShowTable')}
          </button>
        ) : null}
      </div>

      {truncated ? (
        <p className={styles.note}>{t('aiChat.tools.datasourceTruncated', { count: rowCount })}</p>
      ) : null}
      {note ? <p className={styles.note}>{note}</p> : null}
      {executedAt ? (
        <p className={styles.note}>
          {t('aiChat.tools.datasourceAsOf', { date: formatDatasourceExecutedAt(executedAt) })}
        </p>
      ) : null}

      {renderBody()}
    </div>
  );
}

export default AiChatChartFrame;
